import React, { useEffect, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import API from '../../../services/API'
import CrewJourneyDetails from './CrewJourneyDetails'

const getJourneyTime = (journey) =>
  journey?.pickup?.when?.exactTime ||
  journey?.pickup?.when?.patientReadyFrom ||
  journey?.dropoff?.when?.exactTime ||
  journey?.dropoff?.when?.targetArrivalTime

const getPatientName = (journey) => {
  const patient = journey?.booking?.patient || journey?.patient
  return (
    [patient?.demographics?.firstName, patient?.demographics?.lastName]
      .filter(Boolean)
      .join(' ') || 'Unknown patient'
  )
}

const getStopLabel = (stop, fallback) =>
  stop?.name || stop?.town || stop?.postCode || fallback

const needsAcknowledgement = (acknowledgement) =>
  acknowledgement?.required &&
  !acknowledgement?.acknowledgedAt &&
  !acknowledgement?.declinedAt

/**
 * The crew member's own PTS duties (today + upcoming), with shift
 * acknowledgement actions. Reuses the existing /my-shifts acknowledge and
 * decline endpoints, so this stays consistent with the shifts screen. Each
 * duty expands to its planned journeys, which open the full crew details view.
 */
const CrewDutiesList = () => {
  const [duties, setDuties] = useState([])
  const [loading, setLoading] = useState(true)
  const [actioningID, setActioningID] = useState(null)
  const [decliningID, setDecliningID] = useState(null)
  const [declineReason, setDeclineReason] = useState('')
  // Duty whose "declined" the crew member is confirming they want to withdraw
  const [withdrawingID, setWithdrawingID] = useState(null)
  const [error, setError] = useState(null)
  const [expandedDutyID, setExpandedDutyID] = useState(null)
  // dutyID -> { loading, error, journeys }
  const [dutyJourneys, setDutyJourneys] = useState({})
  const [detailJourney, setDetailJourney] = useState(null)

  const toggleDutyJourneys = async (dutyID) => {
    if (expandedDutyID === dutyID) {
      setExpandedDutyID(null)
      return
    }
    setExpandedDutyID(dutyID)
    setDutyJourneys((prev) => ({
      ...prev,
      [dutyID]: { journeys: prev[dutyID]?.journeys || [], loading: true },
    }))
    try {
      const res = await API.get(`/pts/crew/duties/${dutyID}/journeys`)
      // API never rejects on 4xx/5xx — check the status
      setDutyJourneys((prev) => ({
        ...prev,
        [dutyID]:
          res.status === 200
            ? { journeys: res.data?.journeys || [], loading: false }
            : {
                journeys: [],
                loading: false,
                error: 'Unable to load the journeys for this duty.',
              },
      }))
    } catch (e) {
      console.error(e)
      setDutyJourneys((prev) => ({
        ...prev,
        [dutyID]: {
          journeys: [],
          loading: false,
          error: 'Unable to load the journeys for this duty.',
        },
      }))
    }
  }

  const loadDuties = async () => {
    try {
      const res = await API.get('/pts/crew/duties')
      if (res.status === 200) {
        setDuties(res.data?.duties || [])
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDuties()
  }, [])

  const acknowledge = async (shiftID) => {
    setActioningID(shiftID)
    setError(null)
    try {
      const res = await API.post(`/my-shifts/${shiftID}/acknowledge`)
      if (res.status === 200) {
        await loadDuties()
      } else {
        setError(res.data?.error || 'Unable to acknowledge this duty.')
      }
    } catch (e) {
      console.error(e)
      setError('Unable to acknowledge this duty.')
    } finally {
      setActioningID(null)
    }
  }

  const decline = async (shiftID) => {
    setActioningID(shiftID)
    setError(null)
    try {
      const res = await API.post(`/my-shifts/${shiftID}/decline`, {
        reason: declineReason.trim(),
      })
      if (res.status === 200) {
        setDecliningID(null)
        setDeclineReason('')
        await loadDuties()
      } else {
        setError(res.data?.error || 'Unable to decline this duty.')
      }
    } catch (e) {
      console.error(e)
      setError('Unable to decline this duty.')
    } finally {
      setActioningID(null)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={22} />
      </Box>
    )
  }

  if (!duties.length) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        You have no PTS duties today or coming up.
      </Alert>
    )
  }

  return (
    <Stack spacing={1.25} mt={1}>
      <Typography fontWeight={900}>My Duties</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {duties.map((entry) => {
        const ack = entry.acknowledgement
        const pending = needsAcknowledgement(ack)
        const busy = actioningID === entry.shiftID
        const isDeclining = decliningID === entry.shiftID
        const dutyID = entry.duty?._id
        const isExpanded = !!dutyID && expandedDutyID === dutyID
        const planned = dutyJourneys[dutyID]
        return (
          <Card key={entry.shiftID} variant="outlined" sx={{ p: 1.5 }}>
            <Stack spacing={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
              >
                <Box minWidth={0}>
                  <Typography fontWeight={800}>
                    {moment(entry.shiftStart).format('ddd D MMM · HH:mm')}–
                    {moment(entry.shiftEnd).format('HH:mm')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[
                      entry.duty?.vehicle?.callSign,
                      entry.clinicalRole,
                      entry.duty?.baseLocation?.name,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Box>
                <Stack direction="row" gap={0.5} flexWrap="wrap">
                  {entry.duty?.organisation && (
                    <Chip
                      size="small"
                      icon={<AirportShuttleIcon />}
                      label={`${entry.duty.organisation.orgName} dedicated crew`}
                      color="info"
                      sx={{ borderRadius: 1, fontWeight: 700 }}
                    />
                  )}
                  {entry.duty?.journeyCount > 0 && (
                    <Chip
                      size="small"
                      label={`${entry.duty.journeyCount} journey${entry.duty.journeyCount === 1 ? '' : 's'}`}
                      variant="outlined"
                      sx={{ borderRadius: 1, fontWeight: 700 }}
                    />
                  )}
                  {ack?.acknowledgedAt && (
                    <Chip
                      size="small"
                      icon={<CheckCircleOutlineIcon />}
                      label="Acknowledged"
                      color="success"
                      sx={{ borderRadius: 1, fontWeight: 700 }}
                    />
                  )}
                  {ack?.declinedAt && (
                    <Chip
                      size="small"
                      label="Declined"
                      color="error"
                      sx={{ borderRadius: 1, fontWeight: 700 }}
                    />
                  )}
                  {entry.clockedIn && (
                    <Chip
                      size="small"
                      label="On duty"
                      color="success"
                      variant="outlined"
                      sx={{ borderRadius: 1, fontWeight: 700 }}
                    />
                  )}
                </Stack>
              </Stack>
              {entry.duty?.notes && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                >
                  {entry.duty.notes}
                </Typography>
              )}
              {entry.duty?.journeyCount > 0 && (
                <Box>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => toggleDutyJourneys(dutyID)}
                    endIcon={
                      isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                    }
                    sx={{ textTransform: 'none', fontWeight: 800, px: 0.5 }}
                  >
                    {isExpanded ? 'Hide' : 'View'} planned journeys
                  </Button>
                  {isExpanded && planned?.loading && (
                    <Box display="flex" justifyContent="center" py={1}>
                      <CircularProgress size={20} />
                    </Box>
                  )}
                  {isExpanded && planned?.error && (
                    <Alert severity="warning">{planned.error}</Alert>
                  )}
                  {isExpanded && !planned?.loading && !planned?.error && (
                    <Stack divider={<Divider />}>
                      {(planned?.journeys || []).map((journey) => (
                        <Stack
                          key={journey._id}
                          direction="row"
                          spacing={1.25}
                          alignItems="center"
                          role="button"
                          tabIndex={0}
                          onClick={() => setDetailJourney(journey)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              setDetailJourney(journey)
                            }
                          }}
                          sx={{
                            py: 0.9,
                            px: 0.5,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: '#F1F5F9' },
                          }}
                        >
                          <Typography sx={{ fontWeight: 900, minWidth: 42 }}>
                            {getJourneyTime(journey)
                              ? moment(getJourneyTime(journey)).format('HH:mm')
                              : '--:--'}
                          </Typography>
                          <Box flex={1} minWidth={0}>
                            <Typography noWrap sx={{ fontWeight: 800 }}>
                              {getPatientName(journey)}
                            </Typography>
                            <Typography
                              noWrap
                              variant="caption"
                              color="text.secondary"
                              component="div"
                            >
                              {getStopLabel(journey.pickup, 'Pickup')} →{' '}
                              {getStopLabel(journey.dropoff, 'Dropoff')}
                              {(journey.booking?.patient || journey.patient)
                                ?.mobility?.category
                                ? ` · ${(journey.booking?.patient || journey.patient).mobility.category}`
                                : ''}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={journey.status}
                            variant="outlined"
                            sx={{ borderRadius: 1, fontWeight: 700 }}
                          />
                          <ChevronRightIcon
                            fontSize="small"
                            sx={{ color: '#94A3B8' }}
                          />
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Box>
              )}
              {pending &&
                (isDeclining ? (
                  <Stack gap={1}>
                    <Alert severity="warning">
                      Your managers will be told and the duty offered to
                      colleagues. You stay assigned to it until a manager
                      reassigns it.
                    </Alert>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      gap={1}
                      alignItems={{ sm: 'center' }}
                    >
                      <TextField
                        size="small"
                        fullWidth
                        autoFocus
                        placeholder="Reason for declining"
                        value={declineReason}
                        onChange={(event) =>
                          setDeclineReason(event.target.value)
                        }
                      />
                      <Stack direction="row" gap={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setDecliningID(null)
                            setDeclineReason('')
                          }}
                        >
                          Back
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          disabled={!declineReason.trim() || busy}
                          onClick={() => decline(entry.shiftID)}
                        >
                          Confirm decline
                        </Button>
                      </Stack>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack direction="row" gap={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={busy}
                      onClick={() => {
                        setDecliningID(entry.shiftID)
                        setDeclineReason('')
                      }}
                    >
                      Decline
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleOutlineIcon />}
                      disabled={busy}
                      onClick={() => acknowledge(entry.shiftID)}
                    >
                      Accept duty
                    </Button>
                  </Stack>
                ))}
              {ack?.declinedAt &&
                (withdrawingID === entry.shiftID ? (
                  <Stack gap={1}>
                    <Alert severity="info">
                      This confirms you will work this duty. Your managers
                      will be told, and if declining put it up for a swap that
                      offer is withdrawn.
                    </Alert>
                    <Stack direction="row" gap={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setWithdrawingID(null)}
                      >
                        Back
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={busy}
                        onClick={() => {
                          setWithdrawingID(null)
                          acknowledge(entry.shiftID)
                        }}
                      >
                        Confirm I can attend
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      disabled={busy}
                      onClick={() => setWithdrawingID(entry.shiftID)}
                    >
                      I can attend after all
                    </Button>
                  </Stack>
                ))}
            </Stack>
          </Card>
        )
      })}
      <CrewJourneyDetails
        open={!!detailJourney}
        journey={detailJourney}
        onClose={() => setDetailJourney(null)}
      />
    </Stack>
  )
}

export default CrewDutiesList
